import Modal from './Modal';
import {isEqual} from 'lodash';
import React, {memo, useState, useRef, useEffect, useCallback, cloneElement} from 'react';
import styled from 'styled-components';
import {Subject} from 'rxjs';
import {useSelector} from 'react-redux';
import ProgressLoader from '../BaseComponentWrappers/MotifProgressLoader';

const modalSubject = new Subject({
	modalsize: 'sm', // sm | md | lg | xl
	showButtons: true,
	closeBtnTitle: 'Close',
	title: 'Title',
	closeTitle: 'Close',
	hideConfirmButton: true,
	hideCancelButton: false,
	showSaveAndCreateNewButton: false,
	confirmBtnTitle: 'Confirm',
	disableClose: false,
	okTitle: 'Save',
	modalContainerClass: 'ReactiveModalContainer',
	modalbodystyle: 'ReactiveModalBody',
	height: '100%',
	width: '100%',
	component: null,
	variant: 'slideout'
});

const modalObservable = modalSubject.asObservable();

const modalResponseSubject = new Subject({
	event: null
});

const modalResponse = modalResponseSubject.asObservable();

function ReactiveModal() {
	const [showModal, setShowModal] = useState(false);
	const [modalProps, setModalProps] = useState({});
	const [enableConfirm, setEnableConfirm] = useState(false);
	const [enableConfirmClose, setEnableConfirmClose] = useState(false);

	const isLoading = useSelector((store) => store.isLoading);

	const subscriptionRef = useRef(null);

	const childrenRef = useRef();

	useEffect(() => {
		subscriptionRef.current = modalObservable.subscribe({
			next: (args) => {
				setShowModal(true);
				setModalProps(args);
			}
		});

		return () => {
			if (subscriptionRef.current !== null) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	const onConfirm = useCallback(
		(event) => {
			modalResponseSubject.next({type: 'confirm', event});

			const closeModal = () => {
				setShowModal(false);
				setEnableConfirm(false);
				setModalProps({});
			};

			if (childrenRef.current && childrenRef.current.onConfirm) {
				const close = childrenRef.current.onConfirm();

				if (typeof close === 'object') {
					close?.then(() => closeModal());
				}

				if (close === true) {
					closeModal();
				}
			} else {
				closeModal();
			}
		},
		[childrenRef]
	);

	const onHide = useCallback(
		(event) => {
			modalResponseSubject.next({type: 'close', event});

			setShowModal(false);
			setEnableConfirm(false);
			setModalProps({});

			if (childrenRef.current && childrenRef.current.onClose) {
				childrenRef.current.onClose();
			}
		},
		[childrenRef]
	);

	const props = {
		...modalProps,
		ref: childrenRef,
		setEnableConfirm,
		setEnableConfirmClose
	};

	return (
		<StyledModal
			show={showModal}
			{...modalProps}
			isCancelButtonDisabled={isLoading}
			isOkButtonDisabled={!enableConfirm || isLoading}
			isSaveAndCreateNewButtonDisabled={!enableConfirmClose || isLoading}
			onHide={onHide}
			onOkClick={onConfirm}
		>
			<StyledModalBody className="StyledModalBody" height={modalProps.height} width={modalProps.width}>
				{isLoading && <ProgressLoader show={isLoading} />}
				<section className="body">
					{modalProps.component ? cloneElement(modalProps.component, props) : '[Developer Exception]: Need JSX component as component prop on toggleModal'}
				</section>
			</StyledModalBody>
		</StyledModal>
	);
}

function toggleModal(props) {
	modalSubject.next(props);
}

export {modalResponse, toggleModal};

export default memo(ReactiveModal, isEqual);

const StyledModal = styled(Modal)`
	display: flex;

	&.motif-modal-content {
		&.sm-custom {
			width: var(--px-550) !important;
		}

		&.md-custom {
			max-width: var(--px-1400) !important;
			max-height: var(--px-1350) !important;
		}
	}
`;

const StyledModalBody = styled.section`
	display: flex;
	flex-flow: column;
	width: ${({width}) => width ?? '100%'};
	height: ${({height}) => height ?? '100%'};
	padding: 0;

	.motif-toast-fixed-top {
		position: relative !important;
		top: calc(var(--px-10) * -1) !important;
		left: 0 !important;
		width: 100% !important;
	}

	.motif-progress-loader {
		opacity: 0.8;
	}

	.body {
		display: flex;
		width: 100%;
		height: 100%;
	}
`;
